import { useCallback, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from "react";

import type { ViewportController } from "./viewport-controller";

/** Custom scrollbars for a ViewportController-driven view. The viewport is
 *  CSS-transformed (no native scroll), so the tracks/thumbs are positioned
 *  imperatively from the controller's transform and drag to pan. Shared by every
 *  world-transform reader (PdfViewer, CanvasView) so there is one copy of the
 *  ~80 lines instead of a paste per view.
 *
 *  Pairs with {@link useViewportScrollbars}: the hook owns the refs + the
 *  imperative `updateScrollbars`/drag logic and hands this component the wiring. */
export function ViewportScrollbars({
  vTrackRef,
  vThumbRef,
  hTrackRef,
  hThumbRef,
  onVThumbDown,
  onHThumbDown,
}: {
  vTrackRef: RefObject<HTMLDivElement>;
  vThumbRef: RefObject<HTMLDivElement>;
  hTrackRef: RefObject<HTMLDivElement>;
  hThumbRef: RefObject<HTMLDivElement>;
  onVThumbDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onHThumbDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
}) {
  return (
    <>
      <div ref={vTrackRef} className="gv-scrollbar gv-scrollbar-v" style={{ opacity: 0 }}>
        <div ref={vThumbRef} className="gv-scrollbar-thumb" onPointerDown={onVThumbDown} />
      </div>
      <div ref={hTrackRef} className="gv-scrollbar gv-scrollbar-h" style={{ opacity: 0 }}>
        <div ref={hThumbRef} className="gv-scrollbar-thumb" onPointerDown={onHThumbDown} />
      </div>
    </>
  );
}

/** Wire the scrollbars to a controller. Returns a stable `updateScrollbars`
 *  (call it from the controller's throttled onChange — it reads the controller's
 *  metrics and sizes/positions the thumbs) plus the ready-to-render element.
 *  `updateScrollbars` only reads refs, so it stays referentially stable and can
 *  be captured once when the controller is constructed. */
export function useViewportScrollbars(controllerRef: RefObject<ViewportController | null>): {
  updateScrollbars: () => void;
  scrollbars: JSX.Element;
} {
  const vTrackRef = useRef<HTMLDivElement | null>(null);
  const vThumbRef = useRef<HTMLDivElement | null>(null);
  const hTrackRef = useRef<HTMLDivElement | null>(null);
  const hThumbRef = useRef<HTMLDivElement | null>(null);

  // Size + position the thumbs from the controller's transform. Each thumb's
  // length is the viewport/content ratio; its offset is how far the content is
  // scrolled. Tracks hide when the axis fully fits.
  const updateScrollbars = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller) return;
    const m = controller.metrics;
    const axis = (
      track: HTMLDivElement | null,
      thumb: HTMLDivElement | null,
      content: number,
      view: number,
      t: number,
      vertical: boolean,
    ) => {
      if (!track || !thumb) return;
      if (content <= view + 1) {
        track.style.opacity = "0";
        track.style.pointerEvents = "none";
        return;
      }
      const trackLen = vertical ? track.clientHeight : track.clientWidth;
      const size = view / content; // fraction of the track the thumb fills
      const pos = Math.min(1 - size, Math.max(0, -t / content));
      const lenPx = Math.max(28, size * trackLen); // keep the thumb grabbable
      const offPx = Math.min(trackLen - lenPx, pos * trackLen);
      track.style.opacity = "1";
      track.style.pointerEvents = "auto";
      if (vertical) {
        thumb.style.height = `${lenPx.toFixed(1)}px`;
        thumb.style.transform = `translateY(${offPx.toFixed(1)}px)`;
      } else {
        thumb.style.width = `${lenPx.toFixed(1)}px`;
        thumb.style.transform = `translateX(${offPx.toFixed(1)}px)`;
      }
    };
    axis(vTrackRef.current, vThumbRef.current, m.contentH, m.viewH, m.ty, true);
    axis(hTrackRef.current, hThumbRef.current, m.contentW, m.viewW, m.tx, false);
  }, [controllerRef]);

  // Drag a scrollbar thumb → pan that axis. Dragging the thumb by Δpx moves the
  // content by Δpx·(content/trackLen) in the opposite direction.
  const startScrollDrag = useCallback(
    (vertical: boolean) => (e: ReactPointerEvent<HTMLDivElement>) => {
      const controller = controllerRef.current;
      if (!controller) return;
      e.preventDefault();
      e.stopPropagation();
      const m = controller.metrics;
      const start = vertical ? e.clientY : e.clientX;
      const startT = vertical ? controller.ty : controller.tx;
      const content = vertical ? m.contentH : m.contentW;
      const thumb = e.currentTarget;
      const track = thumb.parentElement;
      const trackLen = Math.max(1, vertical ? (track?.clientHeight ?? 1) : (track?.clientWidth ?? 1));
      try {
        thumb.setPointerCapture(e.pointerId);
      } catch {
        /* best effort */
      }
      const onMove = (ev: PointerEvent) => {
        const cur = vertical ? ev.clientY : ev.clientX;
        // Δtranslate = -Δthumb · content/trackLen (thumb travels the track length).
        const nt = startT - (content * (cur - start)) / trackLen;
        if (vertical) controller.setTransform(controller.tx, nt, controller.scale);
        else controller.setTransform(nt, controller.ty, controller.scale);
      };
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        try {
          thumb.releasePointerCapture(ev.pointerId);
        } catch {
          /* ignore */
        }
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [controllerRef],
  );

  const scrollbars = (
    <ViewportScrollbars
      vTrackRef={vTrackRef}
      vThumbRef={vThumbRef}
      hTrackRef={hTrackRef}
      hThumbRef={hThumbRef}
      onVThumbDown={startScrollDrag(true)}
      onHThumbDown={startScrollDrag(false)}
    />
  );

  return { updateScrollbars, scrollbars };
}
